import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { IVerbaScholaLessonGroup, IVerbaScholaStudentCourse, IVerbaScholaStudentProgress, VerbaScholaApi } from '../api/VerbaScholaApi';
import { VerbaScholaEmptyState, VerbaScholaStudentAvatar } from '../component/VerbaScholaTeacherShell';
import { BLUE, BORDER, ERROR, ERROR_SOFT, ERROR_TEXT, GREEN, INTER, MUTED, NAVY, POPPINS, primaryButtonSx, PROGRESS_TRACK, SLATE, SUCCESS_SOFT, SUCCESS_TEXT, SURFACE_MUTED, SURFACE_SUBTLE, WARNING, WARNING_SOFT, WARNING_TEXT } from '../style/verbaScholaStyles';

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
    return `${count} ${count === 1 ? singular : plural}`;
}

function percentNumber(value: number | null | undefined): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function ProgressBar({ value, color = BLUE, label }: { value: number; color?: string; label: string }) {
    const safeValue = Math.max(0, Math.min(100, Math.round(percentNumber(value))));
    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <LinearProgress
                aria-label={label}
                variant="determinate"
                value={safeValue}
                sx={{ flex: 1, minWidth: 70, height: 6, borderRadius: 3, backgroundColor: PROGRESS_TRACK, '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: color } }}
            />
            <Typography sx={{ width: 34, fontFamily: INTER, fontWeight: 700, fontSize: 11, color: NAVY }}>{safeValue}%</Typography>
        </Stack>
    );
}

type StudentRosterRow = IVerbaScholaStudentCourse & {
    progressPercent: number;
    attemptCount: number;
    accuracyPercent: number | null;
};

function buildStudentRosterRows(enrollments: IVerbaScholaStudentCourse[], lessonGroups: IVerbaScholaLessonGroup[]): StudentRosterRow[] {
    const progressByUser = new Map<number, IVerbaScholaStudentProgress[]>();
    lessonGroups.forEach(group => {
        group.students.forEach(progress => {
            progressByUser.set(progress.student_user_id, [...(progressByUser.get(progress.student_user_id) ?? []), progress]);
        });
    });

    return enrollments.map(enrollment => {
        const progressRows = enrollment.student_user_id == null ? [] : progressByUser.get(enrollment.student_user_id) ?? [];
        const stepCount = progressRows.reduce((sum, progress) => sum + progress.step_count, 0);
        const completedStepCount = progressRows.reduce((sum, progress) => sum + progress.completed_step_count, 0);
        const attemptCount = progressRows.reduce((sum, progress) => sum + progress.attempt_count, 0);
        const accuracyRows = progressRows.filter(progress => progress.accuracy_percent != null);
        const accuracyPercent = accuracyRows.length
            ? accuracyRows.reduce((sum, progress) => sum + percentNumber(progress.accuracy_percent), 0) / accuracyRows.length
            : null;

        return {
            ...enrollment,
            progressPercent: stepCount > 0 ? (completedStepCount / stepCount) * 100 : 0,
            attemptCount,
            accuracyPercent,
        };
    });
}

const EMPTY_STUDENT_FORM = { firstName: '', lastName: '', email: '' };

function studentFormFor(row: StudentRosterRow): typeof EMPTY_STUDENT_FORM {
    return { firstName: row.first_name ?? '', lastName: row.last_name ?? '', email: row.email ?? '' };
}

function studentProgressColor(progress: number): string {
    if (progress < 50) return ERROR;
    if (progress < 70) return WARNING;
    return GREEN;
}

function studentStatus(row: StudentRosterRow): { label: string; color: string; backgroundColor: string } {
    if (row.has_joined === false) {
        // On the roster because a teacher added them, but they have never signed in.
        return { label: 'Invited', color: WARNING_TEXT, backgroundColor: WARNING_SOFT };
    }
    if (row.attemptCount === 0) {
        return { label: 'Not started', color: ERROR_TEXT, backgroundColor: ERROR_SOFT };
    }
    if (row.progressPercent < 70) {
        return { label: 'Needs practice', color: WARNING_TEXT, backgroundColor: WARNING_SOFT };
    }
    return { label: 'On track', color: SUCCESS_TEXT, backgroundColor: SUCCESS_SOFT };
}

interface Props {
    courseId: number;
    enrollments: IVerbaScholaStudentCourse[];
    lessonGroups: IVerbaScholaLessonGroup[];
    saving: boolean;
    onAction: (action: () => Promise<unknown>, successMessage: string, invitationHint?: string) => Promise<boolean>;
}

export default function VerbaScholaTeacherCourseStudentsPage({ courseId, enrollments, lessonGroups, saving, onAction }: Props) {
    const history = useHistory();
    const [studentForm, setStudentForm] = React.useState(EMPTY_STUDENT_FORM);
    const [studentQuery, setStudentQuery] = React.useState('');
    const [studentDialogMode, setStudentDialogMode] = React.useState<'add' | 'edit' | null>(null);
    const [studentEditTarget, setStudentEditTarget] = React.useState<StudentRosterRow | null>(null);
    const [studentRemoveOpen, setStudentRemoveOpen] = React.useState(false);
    const [progressStudentId, setProgressStudentId] = React.useState<number | null>(null);
    const studentRows = buildStudentRosterRows(enrollments, lessonGroups);
    const progressStudent = studentRows.find(row => row.id === progressStudentId);
    const visibleStudentRows = studentRows.filter(row => `${row.display_name ?? ''} ${row.email ?? ''} ${row.student_user_id ?? ''}`.toLowerCase().includes(studentQuery.trim().toLowerCase()));
    const studentHasUnclaimedAccount = studentDialogMode === 'edit' && studentEditTarget?.status === 'accepted' && studentEditTarget?.has_joined === false && studentEditTarget.student_user_id != null;
    const studentIdentityLocked = studentDialogMode === 'edit' && studentEditTarget?.status === 'accepted' && studentEditTarget?.has_joined !== false;
    const studentEmailChanged = studentDialogMode === 'edit' && !studentIdentityLocked && studentForm.email.trim().toLowerCase() !== (studentEditTarget?.email ?? '').trim().toLowerCase();
    const studentFormComplete = studentForm.firstName.trim().length > 0 && studentForm.lastName.trim().length > 0 && studentForm.email.trim().length > 0;

    function closeStudentDialog() {
        setStudentDialogMode(null); setStudentEditTarget(null); setStudentForm(EMPTY_STUDENT_FORM);
    }
    function openAddStudentDialog() {
        setStudentForm(EMPTY_STUDENT_FORM); setStudentEditTarget(null); setStudentDialogMode('add');
    }
    function openEditStudentDialog(row: StudentRosterRow) {
        setStudentForm(studentFormFor(row)); setStudentEditTarget(row); setStudentDialogMode('edit');
    }
    function handleSubmitStudent() {
        if (saving) return;
        const first_name = studentForm.firstName.trim(), last_name = studentForm.lastName.trim(), email = studentForm.email.trim();
        if (!first_name || !last_name || !email) return;
        const editTarget = studentEditTarget;
        const action = editTarget ? () => VerbaScholaApi.updateStudentEnrollment(editTarget.id, { first_name, last_name, email }) : () => VerbaScholaApi.createStudentEnrollment(courseId, { first_name, last_name, email });
        const successMessage = editTarget ? 'Student details saved.' : 'Student added.';
        void onAction(action, successMessage, 'use Resend invitation from this student if needed.').then(success => {
            if (success) closeStudentDialog();
        });
    }
    function handleResendStudentInvitation() {
        if (!studentEditTarget) return;
        const id = studentEditTarget.id; closeStudentDialog();
        void onAction(() => VerbaScholaApi.resendStudentInvitation(id), 'Invitation sent again.');
    }
    function handleRemoveStudent() {
        if (!studentEditTarget) return;
        const id = studentEditTarget.id; setStudentRemoveOpen(false); closeStudentDialog();
        void onAction(() => VerbaScholaApi.deleteStudentEnrollment(id), 'Student removed.');
    }

    return (
        <Box sx={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '20px', overflow: 'hidden' }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5} sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                                <Box>
                                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>Students</Typography>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>{pluralize(studentRows.length, 'student')} enrolled or invited</Typography>
                                </Box>
                                <TextField
                                    value={studentQuery}
                                    onChange={event => setStudentQuery(event.target.value)}
                                    size="small"
                                    placeholder="Search students"
                                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ color: MUTED, fontSize: 19 }} /></InputAdornment> }}
                                    sx={{ width: { xs: '100%', md: 230 }, ml: { md: 'auto !important' }, '& .MuiOutlinedInput-root': { height: 39, borderRadius: '11px', fontFamily: INTER, fontSize: 12.5 } }}
                                />
                                <Button variant="contained" startIcon={<PersonAddAltRoundedIcon />} disabled={saving} onClick={openAddStudentDialog} sx={{ ...primaryButtonSx, borderRadius: '11px', textTransform: 'none' }}>
                                    Add student
                                </Button>
                            </Stack>
        
                            {studentRows.length === 0 ? (
                                <VerbaScholaEmptyState message="No students have been added to this class yet." />
                            ) : (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Box sx={{ minWidth: 820 }}>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 1.4fr) minmax(150px, .8fr) 130px 140px 100px', gap: 2, px: 2.5, py: 1.25, backgroundColor: SURFACE_SUBTLE }}>
                                            {['Student', 'Progress', 'Attempts', 'Status', ''].map(label => (
                                                <Typography key={label || 'actions'} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: SLATE, textTransform: 'uppercase', letterSpacing: 0 }}>{label}</Typography>
                                            ))}
                                        </Box>
                                        {visibleStudentRows.map((row, index) => {
                                            const status = studentStatus(row);
        
                                            return (
                                                <Box key={row.id} sx={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 1.4fr) minmax(150px, .8fr) 130px 140px 100px', gap: 2, alignItems: 'center', px: 2.5, py: 1.5, borderTop: index === 0 ? 0 : `1px solid ${BORDER}` }}>
                                                    <Stack direction="row" alignItems="center" spacing={1.25}>
                                                        <VerbaScholaStudentAvatar label={row.display_name} fallbackId={row.id} index={index} />
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY }}>
                                                                {row.display_name ?? row.email ?? 'Student name unavailable'}
                                                            </Typography>
                                                            <Typography noWrap sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE, mt: 0.2 }}>
                                                                {row.email ?? (row.student_user_id == null ? 'Invitation not yet accepted' : 'Email unavailable')}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                    <ProgressBar label={`${row.display_name ?? row.email ?? 'Student'} course progress`} value={row.progressPercent} color={studentProgressColor(row.progressPercent)} />
                                                    <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE }}>{pluralize(row.attemptCount, 'attempt')}</Typography>
                                                    <Chip label={status.label} size="small" sx={{ justifySelf: 'start', fontFamily: INTER, fontWeight: 700, fontSize: 11, color: status.color, backgroundColor: status.backgroundColor }} />
                                                    <Stack direction="row" spacing={0.25} sx={{ justifySelf: 'start' }}><IconButton
                                                        size="small"
                                                        disabled={saving}
                                                        title={`Edit ${row.display_name ?? 'student'}`}
                                                        aria-label={`Edit ${row.display_name ?? 'student'}`}
                                                        onClick={() => openEditStudentDialog(row)}
                                                        sx={{ minWidth: 44, minHeight: 44, color: SLATE, '&:hover': { color: BLUE } }}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>{row.student_user_id ? <IconButton disabled={saving} aria-label={`Open ${row.display_name ?? row.email ?? 'student'}'s class progress`} onClick={() => setProgressStudentId(row.id)} sx={{ minWidth: 44, minHeight: 44, color: BLUE }}><ChevronRightRoundedIcon /></IconButton>
                                                        : <Typography sx={{ minWidth: 44, fontFamily: INTER, fontSize: 11, color: SLATE }} aria-label="Progress is available after the student joins">Pending</Typography>}</Stack>
                                                </Box>
                                            );
                                        })}
                                        {visibleStudentRows.length === 0 && (
                                            <Typography sx={{ p: 3, textAlign: 'center', fontFamily: INTER, fontSize: 12.5, color: SLATE }}>
                                                No students match "{studentQuery}".
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                            <Dialog open={Boolean(progressStudent)} onClose={() => setProgressStudentId(null)} fullWidth maxWidth="sm" aria-labelledby="class-student-progress-title" PaperProps={{ sx: { borderRadius: '20px' } }}>
                                <DialogTitle id="class-student-progress-title" sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>{progressStudent ? `${progressStudent.display_name ?? progressStudent.email ?? 'Student'}'s class progress` : 'Class progress'}</DialogTitle>
                                <DialogContent dividers>
                                    {progressStudent && <Stack spacing={2}>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            <Chip label={`${Math.round(progressStudent.progressPercent)}% complete`} sx={{ color: NAVY, bgcolor: SURFACE_SUBTLE, fontFamily: INTER, fontWeight: 700 }} />
                                            <Chip label={pluralize(progressStudent.attemptCount, 'attempt')} sx={{ color: NAVY, bgcolor: SURFACE_SUBTLE, fontFamily: INTER, fontWeight: 700 }} />
                                        </Stack>
                                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 15 }}>Lessons</Typography>
                                        {lessonGroups.length === 0 ? <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 13 }}>No lessons are available in this class yet.</Typography>
                                            : lessonGroups.map(group => {
                                                const progress = group.students.find(student => student.student_user_id === progressStudent.student_user_id);
                                                return <Button key={group.lesson.id} onClick={() => { setProgressStudentId(null); history.push(`/teacher/course/${courseId}/lesson/${group.lesson.id}/student?student=${progressStudent.student_user_id}`); }} endIcon={<ChevronRightRoundedIcon />} sx={{ justifyContent: 'space-between', textAlign: 'left', p: 1.5, border: `1px solid ${BORDER}`, borderRadius: '12px', fontFamily: INTER, textTransform: 'none', color: NAVY }}>
                                                    <Box><Stack direction="row" spacing={0.75} alignItems="center"><Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 13 }}>{group.lesson.title}</Typography>{(group.lesson.is_hidden || group.lesson.status === 'draft') && <Chip size="small" label={group.lesson.status === 'draft' ? 'Unpublished draft' : 'Hidden'} sx={{ height: 21, fontFamily: INTER, fontSize: 10.5, color: WARNING_TEXT, bgcolor: WARNING_SOFT }} />}</Stack><Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12 }}>{progress ? `${progress.completed_step_count} of ${progress.step_count} activities · ${pluralize(progress.attempt_count, 'attempt')}` : 'Not started'}</Typography></Box>
                                                </Button>;
                                            })}
                                    </Stack>}
                                </DialogContent>
                                <DialogActions sx={{ p: 2 }}><Button onClick={() => setProgressStudentId(null)}>Close</Button></DialogActions>
                            </Dialog>
                            <Dialog
                                open={studentDialogMode !== null}
                                onClose={() => {
                                    if (!saving) closeStudentDialog();
                                }}
                                maxWidth="sm"
                                fullWidth
                                PaperProps={{ sx: { borderRadius: '20px' } }}
                            >
                                <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>
                                    {studentDialogMode === 'edit' ? 'Edit student' : 'Add a student'}
                                </DialogTitle>
                                <DialogContent>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 13, lineHeight: 1.55, color: SLATE, mb: 2.5 }}>
                                        {studentIdentityLocked
                                            ? 'This student has signed in and now manages their own name and email.'
                                            : studentDialogMode === 'edit'
                                              ? 'They have not signed in yet, so you can still correct their name or email.'
                                              : 'They are added to the class right away, and emailed a link to set up their account.'}
                                    </Typography>
                                    <Stack spacing={2} sx={{ pt: 0.5 }}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                            <TextField
                                                label="First name"
                                                value={studentForm.firstName}
                                                onChange={event => setStudentForm(current => ({ ...current, firstName: event.target.value }))}
                                                disabled={saving || studentIdentityLocked}
                                                fullWidth
                                                autoFocus={!studentIdentityLocked}
                                            />
                                            <TextField
                                                label="Last name"
                                                value={studentForm.lastName}
                                                onChange={event => setStudentForm(current => ({ ...current, lastName: event.target.value }))}
                                                disabled={saving || studentIdentityLocked}
                                                fullWidth
                                            />
                                        </Stack>
                                        <TextField
                                            label="Email"
                                            type="email"
                                            value={studentForm.email}
                                            onChange={event => setStudentForm(current => ({ ...current, email: event.target.value }))}
                                            onKeyDown={event => { if (event.key === 'Enter') handleSubmitStudent(); }}
                                            disabled={saving || studentIdentityLocked}
                                            helperText={
                                                studentIdentityLocked
                                                    ? 'Only the student can change this.'
                                                    : studentEmailChanged
                                                      ? 'Saving sends a new invitation to this email.'
                                                      : 'The student signs in with this address.'
                                            }
                                            fullWidth
                                        />
                                        {saving && !studentIdentityLocked && (
                                            <Stack direction="row" alignItems="center" spacing={1} role="status" aria-live="polite">
                                                <CircularProgress size={16} aria-label="Saving student details" sx={{ color: BLUE }} />
                                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>
                                                    {studentDialogMode === 'edit' ? 'Saving student details...' : 'Adding student and sending invitation...'}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {studentDialogMode === 'edit' && studentHasUnclaimedAccount && (
                                            <Box sx={{ p: 1.5, borderRadius: '11px', backgroundColor: SURFACE_MUTED }}>
                                                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: NAVY }}>Invitation not accepted yet</Typography>
                                                <Typography sx={{ fontFamily: INTER, fontSize: 11.5, lineHeight: 1.5, color: SLATE, mt: 0.4 }}>
                                                    They have not set a password since the invitation was sent.
                                                </Typography>
                                                <Button
                                                    disabled={saving}
                                                    onClick={handleResendStudentInvitation}
                                                    sx={{ mt: 0.75, px: 0, color: BLUE, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, textTransform: 'none' }}
                                                >
                                                    Resend invitation
                                                </Button>
                                            </Box>
                                        )}
                                    </Stack>
                                </DialogContent>
                                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                                    {studentDialogMode === 'edit' && (
                                        <Button
                                            disabled={saving}
                                            onClick={() => setStudentRemoveOpen(true)}
                                            sx={{ mr: 'auto', fontFamily: INTER, fontWeight: 700, color: ERROR_TEXT, textTransform: 'none', '&:hover': { backgroundColor: ERROR_SOFT } }}
                                        >
                                            Remove from class
                                        </Button>
                                    )}
                                    <Button disabled={saving && !studentIdentityLocked} onClick={closeStudentDialog} sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>
                                        {studentIdentityLocked ? 'Close' : 'Cancel'}
                                    </Button>
                                    {!studentIdentityLocked && (
                                        <Button variant="contained" disabled={saving || !studentFormComplete} onClick={handleSubmitStudent} sx={{ ...primaryButtonSx, px: 2.5, textTransform: 'none' }}>
                                            {saving && <CircularProgress size={18} aria-label="Saving student" color="inherit" sx={{ mr: 1 }} />}
                                            {saving ? (studentDialogMode === 'edit' ? 'Saving...' : 'Adding...') : (studentDialogMode === 'edit' ? 'Save changes' : 'Add student')}
                                        </Button>
                                    )}
                                </DialogActions>
                            </Dialog>
        
                            <Dialog open={studentRemoveOpen} onClose={() => setStudentRemoveOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
                                <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>
                                    Remove {studentEditTarget?.display_name ?? 'this student'} from this class?
                                </DialogTitle>
                                <DialogContent>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 13, lineHeight: 1.55, color: SLATE }}>
                                        They lose access to these lessons and stop appearing in class reports. Their completed work and speaking scores are kept, and you can add them back with the same email.
                                    </Typography>
                                </DialogContent>
                                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                                    <Button onClick={() => setStudentRemoveOpen(false)} sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
                                    <Button
                                        variant="contained"
                                        disabled={saving}
                                        onClick={handleRemoveStudent}
                                        sx={{ fontFamily: INTER, fontWeight: 700, px: 2.5, textTransform: 'none', backgroundColor: ERROR_TEXT, color: '#fff', boxShadow: 'none', '&:hover': { backgroundColor: ERROR_TEXT, filter: 'brightness(0.9)', boxShadow: 'none' } }}
                                    >
                                        Remove student
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Box>
    );
}
